import React, { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import SwitchInput from '../../components/SwitchInput/SwitchInput';
import { getConfig, updateConfig, testNow } from '../../services/AlertsService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';

const hh = (h) => `${String(h).padStart(2, '0')}:00`;

function Alerts() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const tv = (id, values) => formatMessage({ id }, values);

    const [cfg, setCfg] = useState({ enabled: false, viaTelegram: true, viaPush: true, startHour: 10, endHour: 17, intervalHours: 1, tz: 'Europe/Berlin' });
    const [notification, setNotification] = useState({});

    useEffect(() => {
        getConfig()
            .then(c => {
                // Deep link from the Today empty-state ("Notify me…") pre-enables alerts.
                const enable = new URLSearchParams(window.location.search).get('enable') === '1';
                if (enable && !c.enabled) {
                    updateConfig({ ...c, enabled: true })
                        .then(saved => { setCfg(saved); setNotification({ type: 'success', text: t('alerts.enabledViaLink') }); })
                        .catch(() => setCfg(c));
                } else {
                    setCfg(c);
                }
            })
            .catch(err => logger.error(err.response ? err.response.data : err.message));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function patch(p) { setCfg(prev => ({ ...prev, ...p })); }

    function onSave() {
        updateConfig(cfg)
            .then(saved => { setCfg(saved); setNotification({ type: 'success', text: t('alerts.saved') }); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onTest() {
        testNow()
            .then(r => setNotification({ type: r.sent ? 'success' : 'warning', text: r.sent ? t('alerts.testSent') : t('alerts.testNoData') }))
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    const start = Number.isFinite(cfg.startHour) ? cfg.startHour : 10;
    const end = Number.isFinite(cfg.endHour) ? cfg.endHour : 17;
    const every = cfg.intervalHours || 1;

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="py-4">
                    <h1 className="h4 mb-1">{t('alerts.title')}</h1>
                    <p className="mb-0 text-muted">{t('alerts.subtitle')}</p>
                </div>

                <div className="row">
                    <div className="col-12 col-xl-7">
                        <div className="card card-body border-0 shadow mb-4">
                            <div className="mb-3">
                                <SwitchInput id="enabled" text={t('alerts.enable')} isChecked={cfg.enabled} onChange={e => patch({ enabled: e.target.value })} />
                            </div>

                            <hr className="my-2" />

                            <h2 className="h6 mt-2">{t('alerts.channels')}</h2>
                            <div className="mb-2">
                                <SwitchInput id="viaTelegram" text="Telegram" isChecked={cfg.viaTelegram} onChange={e => patch({ viaTelegram: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <SwitchInput id="viaPush" text={t('alerts.push')} isChecked={cfg.viaPush} onChange={e => patch({ viaPush: e.target.value })} />
                            </div>

                            <hr className="my-2" />

                            {/* Office / quiet-hours window — all times Central European (Berlin). */}
                            <div className="d-flex align-items-center justify-content-between mt-2">
                                <h2 className="h6 mb-0">{t('alerts.officeHours')}</h2>
                                <span className="badge bg-tertiary text-dark">CET/CEST · Berlin</span>
                            </div>
                            <p className="small text-muted mb-3">{t('alerts.berlinNote')}</p>

                            <div className="gh-grid mb-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))' }}>
                                <div>
                                    <label className="form-label small text-muted">{t('alerts.from')}</label>
                                    <select className="form-select" value={start} onChange={e => patch({ startHour: parseInt(e.target.value) })}>
                                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hh(h)}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label small text-muted">{t('alerts.to')}</label>
                                    <select className="form-select" value={end} onChange={e => patch({ endHour: parseInt(e.target.value) })}>
                                        {Array.from({ length: 24 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{hh(h)}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label small text-muted">{t('alerts.every')}</label>
                                    <select className="form-select" value={every} onChange={e => patch({ intervalHours: parseInt(e.target.value) })}>
                                        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} {t('alerts.hours')}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="alert alert-info py-2 px-3 small mb-3" role="status">
                                {tv('alerts.summary', { from: hh(start), to: hh(end), n: every })}
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                <button className="btn btn-gray-800" type="button" onClick={onSave}>{t('common.save')}</button>
                                <button className="btn btn-outline-primary" type="button" onClick={onTest}>{t('alerts.notifyNow')}</button>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-xl-5">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h6">{t('alerts.howTitle')}</h2>
                            <p className="small text-muted mb-0">{t('alerts.howBody')}</p>
                        </div>
                    </div>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Alerts;
